import { useEffect, useState } from "react";
import { X, Download, FileArchive } from "lucide-react";
import { toast } from "sonner";
import { API, authHeaders } from "../lib/supabase";
import { useOnline } from "../lib/net";
import { markExported } from "./ExportNudge";

const TZ = Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
const label = (ym) => new Date(`${ym}-01T00:00:00`).toLocaleDateString(undefined, { month: "long", year: "numeric" });
const lastMonth = () => { const d = new Date(); d.setDate(1); d.setMonth(d.getMonth() - 1); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`; };
const recentMonths = (n = 12) => { const out = []; const d = new Date(); d.setDate(1); for (let i = 0; i < n; i++) { out.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`); d.setMonth(d.getMonth() - 1); } return out; };

export const downloadExport = async (month) => {
  const r = await fetch(`${API}/jobs/export?month=${month}&tz=${encodeURIComponent(TZ)}`, { headers: await authHeaders() });
  if (r.status === 204) return 0;
  if (!r.ok) throw new Error((await r.json().catch(() => ({}))).detail || "Export failed");
  const count = Number(r.headers.get("X-Export-Count") || 0);
  const name = /filename="?([^";]+)"?/.exec(r.headers.get("Content-Disposition") || "")?.[1] || `dropit-jobs-${month}.zip`;
  const url = URL.createObjectURL(await r.blob());
  const a = document.createElement("a"); a.href = url; a.download = name; document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 60000);
  return count;
};

export const ExportSheet = ({ onClose, initialMonth }) => {
  const online = useOnline();
  const [months, setMonths] = useState(null);
  const [month, setMonth] = useState(initialMonth || lastMonth());
  const [busy, setBusy] = useState(false);
  const [empty, setEmpty] = useState("");
  useEffect(() => {
    if (!online) return;
    (async () => {
      try {
        const r = await fetch(`${API}/jobs/export/months?tz=${encodeURIComponent(TZ)}`, { headers: await authHeaders() });
        const d = await r.json(); const m = Object.fromEntries((d.months || []).map((x) => [x.month, x.count])); setMonths(m);
        if (!initialMonth && !m[lastMonth()] && d.months?.length) setMonth(d.months[0].month);
      } catch { setMonths({}); }
    })();
  }, [online]); // eslint-disable-line react-hooks/exhaustive-deps
  const count = months?.[month] || 0;
  const go = async () => {
    setBusy(true); setEmpty("");
    try {
      const n = await downloadExport(month);
      if (n === 0) setEmpty(`No completed jobs in ${label(month)}`); else { markExported(month); toast.success(`Exported ${n} job${n === 1 ? "" : "s"} for ${label(month)}`); }
    } catch (e) { toast.error(e.message); }
    setBusy(false);
  };
  return (
    <div className="sheet-backdrop" onClick={onClose} data-testid="export-sheet-backdrop">
      <div className="sheet" onClick={(e) => e.stopPropagation()} data-testid="export-sheet">
        <div className="flex items-center justify-between"><p className="eyebrow flex items-center gap-1"><FileArchive size={12} /> Monthly export</p><button type="button" className="icon-btn" onClick={onClose} aria-label="Close" data-testid="export-sheet-close"><X size={16} /></button></div>
        <h2 className="card-title">Completed job reports</h2>
        <p className="text-sm text-muted mt-1">One ZIP with a PDF per completed job plus <span className="notranslate" translate="no">summary.csv</span> and <span className="notranslate" translate="no">summary.pdf</span> — handy for invoicing.</p>
        <label className="block mt-4 text-sm font-semibold" htmlFor="export-month">Month</label>
        <select id="export-month" className="field mt-1" value={month} onChange={(e) => { setMonth(e.target.value); setEmpty(""); }} data-testid="export-month-select">
          {Array.from(new Set([...recentMonths(), ...Object.keys(months || {})])).sort().reverse().map((ym) => (
            <option key={ym} value={ym} disabled={months ? !months[ym] : false} data-testid={`export-month-${ym}`}>{label(ym)}{months ? ` · ${months[ym] || 0} job${months[ym] === 1 ? "" : "s"}` : ""}</option>
          ))}
        </select>
        {empty && <p className="text-sm text-muted mt-3" data-testid="export-empty">{empty}</p>}
        <button type="button" className="btn-primary mt-4 flex items-center justify-center gap-2" onClick={go} aria-disabled={!online || busy || (months && !count) ? "true" : undefined} disabled={busy || !online || (months && !count)} data-testid="export-download-button">
          <Download size={16} /> {busy ? "Building ZIP…" : !online ? "Export needs internet" : `Download ZIP${months ? ` · ${count} job${count === 1 ? "" : "s"}` : ""}`}
        </button>
        {!online && <p className="text-xs text-muted mt-2 text-center" data-testid="export-offline-hint">Export needs internet</p>}
      </div>
    </div>
  );
};
